import type { GlyphContours, GlyphSource } from './outline.js';
import type { Point } from '../contract/types.js';

/**
 * A `GlyphSource` backed by a real font file.
 *
 * Takes bytes rather than a path or a URL, so this module works unchanged in
 * Node and in the browser and neither one leaks into the other. The caller reads
 * a file or fetches one; this turns it into outlines.
 *
 * **`opentype.js` is imported dynamically and that is deliberate.** The font is
 * only needed when an operator asks for outlines, and the library is dead weight
 * in a bundle that never does. A dynamic import keeps both out of the initial
 * download, which matters here more than usual: the font this is used with is
 * 1.5 MB.
 */

/**
 * How far a chord may sit from the curve it replaces, as a fraction of the em.
 *
 * A glyph is mostly curves and a DXF polyline has none, so every curve becomes a
 * chord run. 1/1000 of the em means a 10 mm character is drawn to 0.01 mm, which
 * is a twentieth of the 0.2 mm floor the matching tolerance never goes below and
 * finer than any plotter this output reaches.
 *
 * **This is an error budget and not a step count, and the difference cost a
 * factor of twenty.** The first version of this constant was documented exactly
 * as it is here — "expressed against the em rather than as a count of segments
 * per curve, because a curve's length varies by two orders of magnitude between
 * a comma and the outer stroke of a kanji" — and then implemented as
 * `steps = ceil(1 / tolerance)`, a fixed 200 segments for every curve whatever
 * its size. One glyph came back as 3,244 points and a drawing's DXF grew 517x
 * where ADR 0008 had costed roughly 100.
 *
 * A docstring that describes the intent is not an implementation of it.
 */
const FLATTENING_TOLERANCE_EM = 1 / 1000;

/**
 * Segments for one curve, from how far it actually bends.
 *
 * For a quadratic the chord error is bounded by `|P0 - 2·P1 + P2| / (8·n²)`, and
 * for a cubic by three quarters of the same quantity over the worse of its two
 * second differences. Both invert to `n = sqrt(deviation / (k · tolerance))`,
 * which is why a straight-ish curve costs two segments and a bowl costs twenty.
 *
 * Clamped at 64 so a pathological control polygon cannot produce a megabyte of
 * vertices for one character.
 */
function segmentsFor(deviation: number, tolerance: number): number {
  if (!(deviation > 0)) return 1;
  return Math.max(1, Math.min(64, Math.ceil(Math.sqrt(deviation / (8 * tolerance)))));
}

interface OpenTypePathCommand {
  type: 'M' | 'L' | 'C' | 'Q' | 'Z';
  x?: number;
  y?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}

export async function glyphSourceFromFont(
  bytes: ArrayBuffer,
  name: string,
): Promise<GlyphSource> {
  // The package is CommonJS with an ESM wrapper, so `parse` sits on the default
  // export under Node and on the namespace under some bundlers. Both are checked
  // rather than one being assumed, because the failure is a runtime TypeError in
  // whichever environment was not the one it was written on.
  // A plain static specifier, so the bundler can see it, split it into its own
  // chunk and rewrite it for the browser. It produces a 242 kB chunk that is
  // fetched only when an operator asks for outlines.
  //
  // **The first attempt hid this behind a computed specifier and `@vite-ignore`**
  // to silence TS7016, which worked: the type error went away, the bundle built,
  // and the import survived into the browser as the bare string `opentype.js`,
  // which no browser can resolve. It would have thrown the first time the box was
  // ticked, and nothing in the build would have said so. `@types/opentype.js` is
  // typed for the 1.x line against a 2.x dependency, which is why the shape this
  // file actually relies on is still asserted below rather than taken on trust.
  const loaded = (await import('opentype.js')) as unknown as OpenTypeModule;
  const parse = loaded.parse ?? loaded.default?.parse;
  if (typeof parse !== 'function') {
    throw new Error('opentype.js exposed no parse function; the outline source cannot be built');
  }
  const font = parse(bytes);
  const unitsPerEm = font.unitsPerEm;
  const tolerance = FLATTENING_TOLERANCE_EM * unitsPerEm;

  // Outlines are asked for repeatedly — a drawing says 図番 in several places —
  // and flattening is the expensive half. Keyed by character because that is
  // what the port's interface is in terms of.
  const cache = new Map<string, GlyphContours | null>();

  return {
    name,
    unitsPerEm,
    outline(character: string): GlyphContours | null {
      const hit = cache.get(character);
      if (hit !== undefined) return hit;
      const value = build(font, character, tolerance);
      cache.set(character, value);
      return value;
    },
  };
}

interface OpenTypeGlyph {
  index: number;
  advanceWidth?: number;
  getPath(x: number, y: number, size: number): { commands: OpenTypePathCommand[] };
}

interface OpenTypeFont {
  unitsPerEm: number;
  charToGlyph(character: string): OpenTypeGlyph;
}

interface OpenTypeModule {
  parse?: (bytes: ArrayBuffer) => OpenTypeFont;
  default?: { parse?: (bytes: ArrayBuffer) => OpenTypeFont };
}

function build(font: OpenTypeFont, character: string, tolerance: number): GlyphContours | null {
  const glyph = font.charToGlyph(character);
  // Index 0 is `.notdef` — the empty box a font returns for a character it does
  // not have. Treating it as an outline would draw a box where a kanji belongs,
  // which is worse than reporting the character as missing: it looks deliberate.
  if (glyph.index === 0) return null;

  // Asked for at the em size, so the contours come back in the font's own units
  // and `outlineText` can scale them once against the entity's height.
  const path = glyph.getPath(0, 0, font.unitsPerEm);
  const contours: Point[][] = [];
  let current: Point[] = [];
  let cursor: Point = { x: 0, y: 0 };

  const finish = (): void => {
    if (current.length >= 2) contours.push(current);
    current = [];
  };

  for (const command of path.commands) {
    switch (command.type) {
      case 'M':
        finish();
        cursor = { x: command.x!, y: command.y! };
        current = [cursor];
        break;
      case 'L':
        cursor = { x: command.x!, y: command.y! };
        current.push(cursor);
        break;
      case 'Q': {
        const end = { x: command.x!, y: command.y! };
        const control = { x: command.x1!, y: command.y1! };
        const steps = segmentsFor(
          Math.hypot(cursor.x - 2 * control.x + end.x, cursor.y - 2 * control.y + end.y),
          tolerance,
        );
        for (let i = 1; i <= steps; i += 1) {
          const t = i / steps;
          const u = 1 - t;
          current.push({
            x: u * u * cursor.x + 2 * u * t * control.x + t * t * end.x,
            y: u * u * cursor.y + 2 * u * t * control.y + t * t * end.y,
          });
        }
        cursor = end;
        break;
      }
      case 'C': {
        const end = { x: command.x!, y: command.y! };
        const c1 = { x: command.x1!, y: command.y1! };
        const c2 = { x: command.x2!, y: command.y2! };
        const steps = segmentsFor(
          1.5 *
            Math.max(
              Math.hypot(cursor.x - 2 * c1.x + c2.x, cursor.y - 2 * c1.y + c2.y),
              Math.hypot(c1.x - 2 * c2.x + end.x, c1.y - 2 * c2.y + end.y),
            ),
          tolerance,
        );
        for (let i = 1; i <= steps; i += 1) {
          const t = i / steps;
          const u = 1 - t;
          current.push({
            x: u * u * u * cursor.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * end.x,
            y: u * u * u * cursor.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * end.y,
          });
        }
        cursor = end;
        break;
      }
      case 'Z':
        finish();
        break;
    }
  }
  finish();

  // No `contours.length === 0` check here, and that absence is the point. A
  // space is a real glyph with a real advance and no outline, so treating an
  // empty contour list as "no glyph" reports it as a missing character and
  // discards its width — `図番 A-001` would be drawn as `図番A-001`, narrower
  // than the text it stands in for. Absence of a glyph is `.notdef` above, which
  // is a different question and already answered.
  return { contours, advance: glyph.advanceWidth ?? font.unitsPerEm };
}
